import type { PartNode } from '@partful/shared-types';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SetQuantity } from 'src/components/Product/Desktop/Panels/PartsCatalogue/SetQuantity';
import { CartEntry } from 'src/providers/ShoppingCart';
import useShoppingCartMutation, { updateQuantity, without } from 'src/providers/ShoppingCart/useShoppingCartMutation';
import { useTenant } from 'src/providers/Tenant';
import useFormattedPrice from 'src/services/formatCurrency';
import { GA } from 'src/services/gtag';
import { calculateTotalWithPOR } from 'src/services/isPOR';
import {
  BottomSection,
  CardContent,
  DeleteButton,
  Flex,
  FlexRow,
  OneLineEllipsis,
  PartNumber,
  PositionedCard,
  PriceTag,
  QtyText,
  SetQuantityContainer,
  TitleText,
  TwoLineEllipsis,
} from './style';

interface CartItemProps {
  cartItem: CartEntry;
  cart: CartEntry[];
}

export default function CartItem({ cart, cartItem }: CartItemProps) {
  const tenant = useTenant();
  const { t } = useTranslation(['shoppingCart', 'common']);
  const { mutate } = useShoppingCartMutation();
  const { partName, quantity, partNumber, unitPrice } = cartItem;
  const price = calculateTotalWithPOR([{ quantity, unitPrice }]);

  const removeFromCart = useCallback(() => {
    const removeFromCartMutation = () => mutate(without(cart, cartItem));
    removeFromCartMutation();
    const part: PartNode = {
      partNumber,
      name: partName,
      price: unitPrice,
      children: [],
      id: partNumber,
    };
    GA.Ecommerce.removeItemFromCart(tenant, [part], quantity);
  }, [cart, cartItem, mutate, partName, partNumber, quantity, tenant, unitPrice]);

  const setQuantity = useCallback(
    (quantityToAdd: number | '') => {
      const newQuantity = Number(quantityToAdd);
      const setQuantityMutation = (quan: number) => mutate(updateQuantity(cart, quan, cartItem));
      setQuantityMutation(newQuantity);
      const part: PartNode = {
        partNumber,
        name: partName,
        price: unitPrice,
        children: [],
        id: partNumber,
      };

      if (newQuantity < quantity) {
        GA.Ecommerce.removeItemFromCart(tenant, [part], quantity - newQuantity);
      } else {
        GA.Ecommerce.addItemToCart(tenant, [part], newQuantity - quantity);
      }
    },
    [cart, cartItem, partName, quantity, partNumber, unitPrice, mutate, tenant]
  );

  return (
    <PositionedCard data-testid="shopping-cart-panel-item">
      <CardContent>
        <TwoLineEllipsis>
          <TitleText>{partName}</TitleText>
        </TwoLineEllipsis>
        <OneLineEllipsis>
          <PartNumber>{partNumber}</PartNumber>
        </OneLineEllipsis>
        <Flex>
          <PriceTag data-testid="cart-item-panel-price">
            {useFormattedPrice({ price, shortLabel: true, smallFont: true })}
          </PriceTag>
          <BottomSection>
            <SetQuantityContainer>
              <QtyText>{t('qty')}:</QtyText>
              <SetQuantity quantity={quantity} setQuantity={setQuantity} size="small" />
            </SetQuantityContainer>
            <FlexRow>
              <DeleteButton
                aria-label="delete"
                onClick={() => removeFromCart()}
                data-testid="shopping-cart-panel-item-delete-button"
              >
                {t('common:delete')}
              </DeleteButton>
            </FlexRow>
          </BottomSection>
        </Flex>
      </CardContent>
    </PositionedCard>
  );
}
